import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { useWorkouts } from '@/hooks/useWorkouts';
import { useHealth } from '@/hooks/useHealth';
import { useBody } from '@/hooks/useBody';
import { formatDuration } from '@/utils/format';
import { STEPS_GOAL } from '@/types/Health';
import type { Workout } from '@/types/Workout';

/* Default goals for the week rings — same spirit as the app's other default
   goals (Workout.DEFAULT_GOALS / SECTION_DEFAULTS): real progress measured
   against a sensible default, editable later. */
const CARDIO_PER_WEEK = 3;
const STRENGTH_PER_WEEK = 3;
const STEPS_PER_DAY = STEPS_GOAL;
const MOVE_KCAL_PER_DAY = 600;

function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round((t.getTime() - firstThursday.getTime()) / 86_400_000 / 7);
}

function startOfWeek(now: Date): Date {
  const day = (now.getDay() + 6) % 7; // 0 = Monday
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(now.getDate() - day);
  return d;
}

const isCardio = (w: Workout) => w.modality !== 'STRENGTH';

function strengthVolumeKg(w: Workout): number {
  let v = 0;
  for (const ex of w.exercises ?? []) {
    for (const s of ex.sets ?? []) v += (s.reps ?? 0) * (s.weightKg ?? 0);
  }
  return v;
}

/** Split "78.4" → { whole: "78", frac: "4" } for the big-number tiles. */
function splitNum(n: number): { whole: string; frac: string } {
  const [whole, frac = '0'] = n.toFixed(1).split('.');
  return { whole: whole ?? '0', frac };
}

function Ring({ pct, label, hot }: { pct: number; label: string; hot?: boolean }) {
  const r = 33;
  const c = 2 * Math.PI * r;
  const off = c * Math.max(0, 1 - pct / 100);
  return (
    <div className="mh-ring">
      <svg viewBox="0 0 84 84">
        <circle className="mh-rbg" cx="42" cy="42" r={r} />
        <circle
          className={`mh-rfg${hot ? ' mh-h' : ''}`}
          cx="42"
          cy="42"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={off}
        />
      </svg>
      <div className={`mh-rpct${hot ? ' mh-h' : ''}`}>{Math.round(pct)}</div>
      <div className="mh-rlab">{label}</div>
    </div>
  );
}

/**
 * Mobile Home cockpit (Variant B). Rendered by {@link HomePage} only for
 * authenticated users on ≤768px; the desktop home (hero + ticker) is
 * untouched. Wired to real data (workouts, health, body); widgets with no
 * backing data yet (next medical test, planned "up next") show an empty/CTA
 * state. Styles: `styles/mobile-home.css`.
 */
export function MobileHome({ name }: { name: string }) {
  const navigate = useNavigate();
  const { workouts } = useWorkouts(true);
  const { metrics } = useHealth(true);
  const { latest: body, targets } = useBody();

  const now = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(now), [now]);

  const firstName = name.trim().split(/\s+/)[0] || name;
  const dateLabel = now.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  const sorted = useMemo(
    () => [...workouts].sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? '')),
    [workouts],
  );
  const last = sorted[0];
  const week = useMemo(
    () => sorted.filter((w) => w.startTime != null && new Date(w.startTime) >= weekStart),
    [sorted, weekStart],
  );
  const weekCardio = useMemo(() => week.filter(isCardio), [week]);
  const weekStrength = useMemo(() => week.filter((w) => w.modality === 'STRENGTH'), [week]);

  const distKm = weekCardio.reduce((s, w) => s + (w.distance ?? 0), 0);
  const volTonnes = weekStrength.reduce((s, w) => s + strengthVolumeKg(w), 0) / 1000;

  const latestHealth = useMemo(
    () => [...metrics].sort((a, b) => (b.metricDate ?? '').localeCompare(a.metricDate ?? ''))[0],
    [metrics],
  );
  const steps = latestHealth?.steps ?? 0;
  const moveKcal = latestHealth?.caloriesActive ?? 0;

  const rings = [
    { label: 'Move', pct: (moveKcal / MOVE_KCAL_PER_DAY) * 100 },
    { label: 'Cardio', pct: (weekCardio.length / CARDIO_PER_WEEK) * 100 },
    { label: 'Strength', pct: (weekStrength.length / STRENGTH_PER_WEEK) * 100 },
    { label: 'Steps', pct: (steps / STEPS_PER_DAY) * 100 },
  ];
  const ringsDone = rings.filter((r) => r.pct >= 100).length;

  // resume card
  const lastIsStrength = last?.modality === 'STRENGTH';
  const lastName = last?.name || last?.workoutTypeName || (lastIsStrength ? 'Strength' : 'Workout');
  const lastDist =
    !lastIsStrength && last?.distance != null ? ` · ${last.distance.toFixed(1)} KM` : '';
  const lastTime = last?.startTime
    ? new Date(last.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : '';

  const weight = body.weight;
  const targetW = targets.weight;
  const weightDelta = weight != null && targetW != null ? weight - targetW : null;

  const vol = splitNum(volTonnes);
  const w = weight != null ? splitNum(weight) : null;

  return (
    <div className="mhome">
      <div className="mh-px">
        <div className="mh-eyebrow">
          Week {isoWeek(now)} · {now.getFullYear()}
          <span className="mh-date">— {dateLabel}</span>
        </div>
        <h1 className="mh-hero">
          Welcome
          <br />
          back,
          <br />
          <span className="mh-accent">{firstName}</span>
        </h1>
        <p className="mh-tagline">
          {week.length > 0 ? (
            <>
              You logged <b>{week.length}</b> {week.length === 1 ? 'session' : 'sessions'} this
              week. Keep the momentum.
            </>
          ) : (
            <>No sessions yet this week — time to start.</>
          )}
        </p>
      </div>

      {/* resume / first-session CTA */}
      <div className="mh-px">
        {last ? (
          <button
            className="mh-resume"
            onClick={() => navigate(lastIsStrength ? '/strength' : '/cardio')}
          >
            <span className="mh-arr">→</span>
            <span className="mh-resume-main">
              <span className="mh-resume-pre">Resume · last session</span>
              <span className="mh-resume-nm">
                {lastName}
                {lastDist}
              </span>
            </span>
            <span className="mh-resume-meta">
              {formatDuration(last.duration)}
              {lastTime && (
                <>
                  <br />
                  <span className="mh-mut">{lastTime}</span>
                </>
              )}
            </span>
          </button>
        ) : (
          <button className="mh-resume mh-empty" onClick={() => navigate('/cardio')}>
            <span className="mh-arr">→</span>
            <span className="mh-resume-main">
              <span className="mh-resume-pre">Get started</span>
              <span className="mh-resume-nm">Log your first session</span>
            </span>
          </button>
        )}
      </div>

      {/* week rings */}
      <div className="mh-px mh-sec">
        <div className="mh-sectlab">
          <span>Week rings</span>
          <em>
            {ringsDone} of {rings.length}
          </em>
        </div>
        <div className="mh-rings">
          {rings.map((r) => (
            <Ring key={r.label} pct={r.pct} label={r.label} hot={r.pct > 100} />
          ))}
        </div>
      </div>

      {/* at a glance */}
      <div className="mh-sec">
        <div className="mh-px">
          <div className="mh-sectlab">
            <span>This week</span>
            <em>at a glance</em>
          </div>
        </div>
        <div className="mh-grid">
          <button className="mh-tile" onClick={() => navigate('/strength')}>
            <div className="mh-th">
              Volume <em>STR</em>
            </div>
            <div className="mh-big">
              {vol.whole}
              <span className="mh-u">.{vol.frac}t</span>
            </div>
            <div className="mh-strap">
              <span>
                {weekStrength.length} {weekStrength.length === 1 ? 'session' : 'sessions'}
              </span>
            </div>
          </button>

          <button className="mh-tile" onClick={() => navigate('/cardio')}>
            <div className="mh-th">
              Distance <em>CAR</em>
            </div>
            <div className="mh-big">
              {Math.round(distKm)}
              <span className="mh-u">km</span>
            </div>
            <div className="mh-strap">
              <span>
                {weekCardio.length} {weekCardio.length === 1 ? 'session' : 'sessions'}
              </span>
            </div>
          </button>

          {w ? (
            <button className="mh-tile" onClick={() => navigate('/body')}>
              <div className="mh-th">
                Weight <em>BODY</em>
              </div>
              <div className="mh-big">
                {w.whole}
                <span className="mh-u">.{w.frac}</span>
              </div>
              <div className="mh-strap">
                <span>{targetW != null ? `target ${targetW.toFixed(1)}` : 'kg'}</span>
                {weightDelta != null && (
                  <span className={weightDelta <= 0 ? 'mh-acc' : 'mh-hotc'}>
                    {weightDelta > 0 ? '+' : ''}
                    {weightDelta.toFixed(1)}
                  </span>
                )}
              </div>
            </button>
          ) : (
            <button className="mh-tile mh-empty" onClick={() => navigate('/body')}>
              <div className="mh-th">
                Weight <em>BODY</em>
              </div>
              <div className="mh-big">
                +<span className="mh-u">ADD</span>
              </div>
              <div className="mh-strap">
                <span>No measurement yet</span>
              </div>
            </button>
          )}

          {/* next medical test — no scheduling data in the backend yet */}
          <button className="mh-tile mh-empty" onClick={() => navigate('/medical')}>
            <div className="mh-th">Next test</div>
            <div className="mh-big">—</div>
            <div className="mh-strap">
              <span>Not scheduled</span>
            </div>
          </button>
        </div>
      </div>

      {/* up next — no planning data in the backend yet → CTA */}
      <div className="mh-px mh-sec">
        <div className="mh-sectlab">
          <span>Up next</span>
          <em>today</em>
        </div>
        <button className="mh-row" onClick={() => navigate('/calendar')}>
          <span className="mh-ic">
            <CalendarDays />
          </span>
          <span className="mh-main">
            <span className="mh-t">Nothing planned</span>
            <span className="mh-s">Open calendar to schedule</span>
          </span>
          <span className="mh-arr2">→</span>
        </button>
      </div>
    </div>
  );
}
